/* eslint-disable @typescript-eslint/no-explicit-any */
// services/searchService.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
// services/searchService.ts
import type { SearchFilters, SearchResult } from "../context/SearchContext";

const API_BASE = import.meta.env.VITE_API_BASE;

export interface SearchResponse {
  success: boolean;
  query: string;
  filters: {
    types: string[];
    category: string;
    location: string;
    salaryRange: string;
    experienceLevel: string;
    datePosted: string;
  };
  results: SearchResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  metadata: {
    typesCount: {
      jobs: number;
      events: number;
      articles: number;
      companies: number;
    };
  };
  cached?: boolean;
  duration?: string;
}

export interface SearchSuggestion {
  type: "job" | "company" | "location" | "category" | "skill";
  text: string;
  category: string;
}

export interface SearchTrends {
  popularCategories: Array<{ name: string; count: number }>;
  popularLocations: Array<{ name: string; count: number }>;
  popularCompanies: Array<{ name: string; count: number }>;
  jobTypes: Array<{ name: string; count: number }>;
  updatedAt: string;
}

export interface SearchHistoryItem {
  query: string;
  filters: Record<string, any>;
  timestamp: string;
}

class SearchService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/search`;
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = localStorage.getItem("authToken");
    const headers = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`Search API error: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Perform global search across all content types
   */
  async globalSearch(
    filters: Partial<SearchFilters> & { page?: number; limit?: number },
  ): Promise<SearchResponse> {
    const {
      query = "",
      searchType = "all",
      category = "",
      location = "",
      salaryRange = "",
      experienceLevel = "",
      datePosted = "",
      page = 1,
      limit = 10,
    } = filters;

    const params = new URLSearchParams({
      query: query.trim(),
      types:
        searchType === "all" ? "jobs,events,articles,companies" : searchType,
      ...(category && { category }),
      ...(location && { location }),
      ...(salaryRange && { salaryRange }),
      ...(experienceLevel && { experienceLevel }),
      ...(datePosted && { datePosted }),
      page: page.toString(),
      limit: limit.toString(),
    });

    return this.fetchWithAuth(`/global?${params}`);
  }

  /**
   * Advanced job search with detailed filters
   */
  async advancedJobSearch(params: {
    query?: string;
    category?: string;
    location?: string;
    employmentType?: string;
    experienceLevel?: string;
    salaryRange?: string;
    remote?: string;
    skills?: string;
    company?: string;
    datePosted?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
  }): Promise<{
    success: boolean;
    results: SearchResult[];
    pagination: SearchResponse["pagination"];
    filters: Record<string, any>;
  }> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, value.toString());
      }
    });

    return this.fetchWithAuth(`/jobs/advanced?${searchParams}`);
  }

  /**
   * Get search suggestions/autocomplete
   */
  async getSearchSuggestions(
    query: string,
    type: string = "all",
  ): Promise<{
    success: boolean;
    suggestions: SearchSuggestion[];
    cached?: boolean;
  }> {
    if (!query.trim() || query.trim().length < 2) {
      return { success: true, suggestions: [] };
    }

    const params = new URLSearchParams({
      query: query.trim(),
      type,
    });

    return this.fetchWithAuth(`/suggestions?${params}`);
  }

  /**
   * Get popular search trends
   */
  async getSearchTrends(): Promise<{
    success: boolean;
    trends: SearchTrends;
    cached?: boolean;
  }> {
    return this.fetchWithAuth("/trends");
  }

  /**
   * Get user's search history
   */
  async getSearchHistory(): Promise<{
    success: boolean;
    history: SearchHistoryItem[];
    cached?: boolean;
  }> {
    return this.fetchWithAuth("/history");
  }

  /**
   * Save search to user's history
   */
  async saveSearch(
    query: string,
    filters: Record<string, any> = {},
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.fetchWithAuth("/history", {
      method: "POST",
      body: JSON.stringify({ query, filters }),
    });
  }

  /**
   * Clear user's search history
   */
  async clearSearchHistory(): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.fetchWithAuth("/history", {
      method: "DELETE",
    });
  }

  /**
   * Search jobs with basic filters
   */
  async searchJobs(params: {
    query?: string;
    location?: string;
    type?: string;
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    jobs: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalJobs: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    search: Record<string, any>;
    code: string;
  }> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, value.toString());
      }
    });

    return this.fetchWithAuth(`/jobs?${searchParams}`);
  }

  /**
   * Search events
   */
  async searchEvents(params: {
    query?: string;
    category?: string;
    location?: string;
    datePosted?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    events: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalEvents: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    search: Record<string, any>;
    code: string;
  }> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, value.toString());
      }
    });

    return this.fetchWithAuth(`/events?${searchParams}`);
  }

  /**
   * Search companies
   */
  async searchCompanies(params: {
    query?: string;
    location?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    companies: any[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCompanies: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    search: Record<string, any>;
    code: string;
  }> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, value.toString());
      }
    });

    return this.fetchWithAuth(`/companies?${searchParams}`);
  }

  /**
   * Get jobs by specific company
   */
  async getJobsByCompany(
    company: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<{
    jobs: any[];
    company: string;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalJobs: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
    code: string;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    return this.fetchWithAuth(
      `/companies/${encodeURIComponent(company)}/jobs?${params}`,
    );
  }

  /**
   * Get similar jobs based on current job
   */
  async getSimilarJobs(
    jobId: string,
    limit: number = 5,
  ): Promise<{
    success: boolean;
    jobs: any[];
    code: string;
  }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
    });

    return this.fetchWithAuth(`/jobs/${jobId}/similar?${params}`);
  }

  /**
   * Get search analytics for admin
   */
  async getSearchAnalytics(timeRange: string = "7d"): Promise<{
    success: boolean;
    analytics: {
      totalSearches: number;
      popularQueries: Array<{ query: string; count: number }>;
      searchTypes: Array<{ type: string; count: number }>;
      noResultQueries: Array<{ query: string; count: number }>;
      timeRange: string;
    };
  }> {
    const params = new URLSearchParams({ timeRange });
    return this.fetchWithAuth(`/analytics?${params}`);
  }
}

// Create singleton instance
export const searchService = new SearchService();

// Export types
// export type {
//   SearchResponse,
//   SearchSuggestion,
//   SearchTrends,
//   SearchHistoryItem,
// };

export default SearchService;
